// API Service Layer for FitPro Hub
// Always use relative URLs to avoid CORS issues with Kubernetes ingress
// (ingress overrides Access-Control-Allow-Origin headers, breaking cross-origin requests)

// Storage key for auth token (centralized)
const AUTH_TOKEN_KEY = 'gym_notebook_auth_token';

class ApiService {
  constructor() {
    this.token = null;
    // Safely access localStorage (might not be available in some contexts)
    try {
      this.token = localStorage.getItem(AUTH_TOKEN_KEY);
    } catch (e) {
      console.warn('localStorage not available');
    }
  }

  setToken(token) {
    this.token = token;
    try {
      if (token) {
        localStorage.setItem(AUTH_TOKEN_KEY, token);
      } else {
        localStorage.removeItem(AUTH_TOKEN_KEY);
      }
    } catch (e) {
      console.warn('localStorage not available');
    }
  }

  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    return headers;
  }

  async request(endpoint, options = {}, retryCount = 0) {
    // Always use relative URL (same-origin) to avoid CORS issues
    const url = endpoint;
    const config = {
      ...options,
      headers: this.getHeaders(),
    };

    try {
      // Add timeout to prevent hanging requests
      // Use longer timeout for AI endpoints (2 minutes)
      const isAiEndpoint = url.includes('/ai/') || url.includes('/ai-advice') || url.includes('/voice-food') || url.includes('/glucose-sim/') || url.includes('/grocery-swap/') || url.includes('/health-coach/');
      const timeoutMs = isAiEndpoint ? 120000 : 30000;
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      
      let response;
      try {
        response = await fetch(url, { ...config, signal: controller.signal });
      } finally {
        clearTimeout(timeoutId);
      }
      
      // Get response status before body is consumed
      const status = response.status;
      const ok = response.ok;
      
      // Read response body — handle platform interceptors that consume the body
      let responseText = '';
      let data = null;
      
      // Clone may fail if body is already disturbed by platform interceptor
      let clone = null;
      try { clone = response.clone(); } catch { /* body already disturbed */ }
      
      // Try multiple strategies to read the body
      try {
        if (!response.bodyUsed) {
          responseText = await response.text();
        } else if (clone) {
          responseText = await clone.text();
        }
      } catch (textErr) {
        if (clone) {
          try { responseText = await clone.text(); } catch { /* exhausted */ }
        }
      }
      
      // If we still have no text, handle based on status
      if (!responseText) {
        console.warn(`[API] Could not read response body for ${endpoint} (status: ${status})`);
        if (status === 401) {
          this.setToken(null);
          throw new Error('Session expired');
        } else if (!ok) {
          throw new Error(`Request failed (${status})`);
        }
        return {};
      }
      
      // Debug log - show more for AI endpoints
      if (isAiEndpoint) {
        console.log(`AI API Response [${endpoint}]: status=${status}, ok=${ok}, length=${responseText.length}`);
      } else {
        console.log(`API Response [${endpoint}]:`, responseText.substring(0, 200));
      }
      
      // Parse JSON if we have content
      if (responseText && responseText.trim()) {
        try {
          data = JSON.parse(responseText);
        } catch (parseError) {
          console.error('JSON parse error:', parseError);
          if (!ok) {
            throw new Error(responseText.length < 100 ? responseText : 'Server error. Please try again.');
          }
        }
      }
      
      // Handle 401 Unauthorized - clear token and throw
      if (status === 401) {
        this.setToken(null);
        const errorMessage = data?.detail || data?.message || 'Invalid credentials';
        throw new Error(errorMessage);
      }
      
      // Handle 5xx errors with retry
      if (status >= 500 && status < 600 && retryCount < 2) {
        console.log(`Server error ${status}, retrying (attempt ${retryCount + 2})...`);
        await new Promise(resolve => setTimeout(resolve, 1000));
        return this.request(endpoint, options, retryCount + 1);
      }
      
      // Handle other error status codes
      if (!ok) {
        if (status >= 500) {
          throw new Error(`Server error (${status}). Please try again in a moment.`);
        }
        throw new Error(data?.detail || data?.message || 'Request failed');
      }
      
      // For successful empty responses (like logout), return empty object
      if (!data && ok) {
        return {};
      }
      
      return data;
    } catch (error) {
      console.error(`API Error [${endpoint}]:`, error);
      
      // Retry on network errors
      if ((error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) && retryCount < 2) {
        console.log(`Network error, retrying (attempt ${retryCount + 2})...`);
        await new Promise(resolve => setTimeout(resolve, 1000));
        return this.request(endpoint, options, retryCount + 1);
      }
      
      // Improve error messages for common issues
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please check your connection and try again.');
      }
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        throw new Error('Network error. Please check your internet connection.');
      }
      throw error;
    }
  }

  // Generic HTTP methods - using v2 API
  async get(endpoint) {
    return this.request(`/api/v2${endpoint}`, {
      method: 'GET',
    });
  }

  async post(endpoint, data) {
    return this.request(`/api/v2${endpoint}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put(endpoint, data) {
    return this.request(`/api/v2${endpoint}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint) {
    return this.request(`/api/v2${endpoint}`, {
      method: 'DELETE',
    });
  }

  // Auth endpoints - using v2 API
  async register(name, email, password) {
    const data = await this.request('/api/v2/auth/register', {
      method: 'POST',
      body: JSON.stringify({ name, email, password }),
    });
    // Save token after successful registration
    if (data.token) {
      this.setToken(data.token);
    }
    return data;
  }

  async login(email, password) {
    const data = await this.request('/api/v2/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    // If 2FA is required, return immediately without setting token
    if (data.requires_2fa) {
      return data;
    }
    if (data.token) {
      this.setToken(data.token);
    }
    return data;
  }

  async processGoogleSession(sessionId) {
    const url = `/api/v2/auth/session?session_id=${sessionId}`;
    console.log('[API] Processing Google session');

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      let response;
      try {
        response = await fetch(url, {
          headers: this.getHeaders(),
          signal: controller.signal
        });
      } finally {
        clearTimeout(timeoutId);
      }

      const status = response.status;
      const ok = response.ok;

      // Read response body safely — platform interceptors may consume the body
      let responseText = '';
      let data = null;

      // Clone may fail if body is already disturbed by platform interceptor
      let clone = null;
      try { clone = response.clone(); } catch { /* body already disturbed */ }

      // Try multiple strategies to read the body
      try {
        if (!response.bodyUsed) {
          responseText = await response.text();
        } else if (clone) {
          responseText = await clone.text();
        }
      } catch (textErr) {
        if (clone) {
          try { responseText = await clone.text(); } catch { /* exhausted */ }
        }
      }

      // Parse JSON
      if (responseText && responseText.trim()) {
        try {
          data = JSON.parse(responseText);
        } catch {
          console.error('[API] Session response JSON parse error');
          data = {};
        }
      } else {
        console.warn('[API] Could not read session response body (status:', status, ')');
        data = {};
      }

      console.log('[API] Session response parsed:', JSON.stringify({
        ok, status,
        requires_2fa: !!data?.requires_2fa,
        has_token: !!data?.token,
        keys: Object.keys(data || {}),
      }));

      if (!ok) {
        throw new Error(data?.detail || data?.message || `Session failed (${status})`);
      }

      if (data.token && !data.requires_2fa) {
        this.setToken(data.token);
      }
      return data;
    } catch (err) {
      console.error('[API] Session processing failed:', err.message);
      throw err;
    }
  }

  async getMe() {
    return this.request('/api/v2/auth/me');
  }

  async logout() {
    try {
      await this.request('/api/v2/auth/logout', { method: 'POST' });
    } finally {
      this.setToken(null);
    }
  }

  // Subscription endpoints - using v2 API
  async getPlans(currency = 'EUR') {
    return this.request(`/api/v2/subscription/plans?currency=${currency}`);
  }

  async createCheckout(plan, currency = 'EUR') {
    return this.request('/api/v2/subscription/checkout', {
      method: 'POST',
      body: JSON.stringify({ plan, currency }),
    });
  }

  async getCheckoutStatus(sessionId) {
    return this.request(`/api/v2/subscription/status/${sessionId}`);
  }

  async createCustomerPortal() {
    return this.request('/api/v2/subscription/portal', {
      method: 'POST',
    });
  }

  async getPaymentHistory() {
    return this.request('/api/v2/subscription/history');
  }

  // Sync endpoints - using v2 API
  async uploadSyncData(data) {
    return this.request('/api/v2/sync/upload', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async downloadSyncData() {
    return this.request('/api/v2/sync/download');
  }

  async getSyncStatus() {
    return this.request('/api/v2/sync/status');
  }

  // Profile endpoints - using v2 API
  async updateProfile(name) {
    return this.request(`/api/v2/user/profile?name=${encodeURIComponent(name)}`, {
      method: 'PUT',
    });
  }

  // Training History endpoints - using v2 API
  async deleteTrainingHistoryEntry(entryId) {
    return this.request(`/api/v2/data/training-history/${entryId}`, {
      method: 'DELETE',
    });
  }

  async clearTrainingHistory() {
    return this.request('/api/v2/data/training-history', {
      method: 'DELETE',
    });
  }

  // Invoice endpoint - using v2 API
  async downloadInvoice(paymentId) {
    const url = `/api/v2/invoice/${paymentId}`;
    const response = await fetch(url, {
      headers: this.getHeaders(),
    });
    
    if (!response.ok) {
      throw new Error('Failed to download invoice');
    }
    
    const blob = await response.blob();
    return blob;
  }
}

export const api = new ApiService();
export default api;
